import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";

import { Observable, of } from "rxjs";
import { catchError, finalize, map, shareReplay, switchMap, take } from "rxjs/operators";

import { SessionStorage } from "@dffedb/util";
import { UmbracoIndstillingerObserver } from "@e-forsyning/common/umbraco-indstillinger";
import { convertAppserverUrlToLocalProxy } from "@e-forsyning/common/util";

import { AuthenticationService } from "./authentication.service";

const CACHE_PREFIX = "tekniker.pwdFeatures.";

export interface TeknikerPasswordCapabilityOptions {
    forsyningId?: string;
    appServerUrl?: string;
    /** Ved værkvalg: spring session-cache over, så et tidligere 404 ikke blokerer. */
    forceRefresh?: boolean;
}

/**
 * ClientApp-først: detekterer om appserver understøtter tekniker-password-API'er
 * (glemt/reset/skift). Ved 404/fejl = legacy mode (fail closed).
 */
@Injectable({ providedIn: "root" })
export class TeknikerPasswordCapabilityService {
    private readonly inFlight = new Map<string, Observable<boolean>>();

    constructor(
        private readonly http: HttpClient,
        private readonly authenticationService: AuthenticationService,
        private readonly indstillinger: UmbracoIndstillingerObserver,
        private readonly sessionStorage: SessionStorage
    ) {}

    public supportsTeknikerPasswordFeatures$(options: TeknikerPasswordCapabilityOptions = {}): Observable<boolean> {
        return this.resolveContext(options).pipe(
            switchMap(({ forsyningId, appServerUrl }) => {
                const cacheKey = this.cacheKey(forsyningId, appServerUrl);
                if (!cacheKey) {
                    return of(false);
                }

                if (!options.forceRefresh) {
                    const cached = this.readCache(cacheKey);
                    // Kun positiv cache genbruges — negativ (404) skal kunne retries efter appserver-opdatering.
                    if (cached === true) {
                        return of(true);
                    }
                }

                const existing = this.inFlight.get(cacheKey);
                if (existing) {
                    return existing;
                }

                const request$ = this.probe(appServerUrl, forsyningId).pipe(
                    map((supported) => {
                        if (supported) {
                            this.writeCache(cacheKey, true);
                        } else {
                            this.clearCache(cacheKey);
                        }
                        return supported;
                    }),
                    catchError(() => {
                        this.clearCache(cacheKey);
                        return of(false);
                    }),
                    finalize(() => this.inFlight.delete(cacheKey)),
                    shareReplay(1)
                );

                this.inFlight.set(cacheKey, request$);
                return request$;
            })
        );
    }

    /** Synkron læsning af cache (kun true betyder kendt support). */
    public isSupportedCached(forsyningId?: string, appServerUrl?: string): boolean | null {
        const key = this.cacheKey((forsyningId || "").trim(), (appServerUrl || "").trim());
        if (!key) {
            return null;
        }
        const cached = this.readCache(key);
        return cached === true ? true : null;
    }

    private resolveContext(
        options: TeknikerPasswordCapabilityOptions
    ): Observable<{ forsyningId: string; appServerUrl: string }> {
        const optionForsyningId = (options.forsyningId || "").trim();
        const optionAppServerUrl = (options.appServerUrl || "").trim();

        if (optionForsyningId || optionAppServerUrl) {
            if (optionAppServerUrl) {
                return of({ forsyningId: optionForsyningId, appServerUrl: optionAppServerUrl });
            }
            return this.authenticationService.hentAppServerUrl(optionForsyningId).pipe(
                map((url) => ({ forsyningId: optionForsyningId, appServerUrl: (url || "").trim() })),
                catchError(() => of({ forsyningId: optionForsyningId, appServerUrl: "" }))
            );
        }

        return this.indstillinger.valueChanges.pipe(
            take(1),
            switchMap((umbraco) => {
                const aktuel = umbraco?.aktuelEforsyning;
                const forsyningId = (aktuel?.forsyningId || "").trim();
                const configuredUrl = (aktuel?.appServerUrl || "").trim();
                if (configuredUrl) {
                    return of({ forsyningId, appServerUrl: configuredUrl });
                }
                if (!forsyningId) {
                    return of({ forsyningId: "", appServerUrl: "" });
                }
                return this.authenticationService.hentAppServerUrl(forsyningId).pipe(
                    map((url) => ({ forsyningId, appServerUrl: (url || "").trim() })),
                    catchError(() => of({ forsyningId, appServerUrl: "" }))
                );
            })
        );
    }

    private probe(appServerUrl: string, forsyningId: string): Observable<boolean> {
        // appServerUrl er allerede resolved i resolveContext — undgå dobbelt GetVaerkSettings.
        const baseUrl = convertAppserverUrlToLocalProxy((appServerUrl || "").trim());
        if (!baseUrl) {
            if (!forsyningId) {
                return of(false);
            }
            return this.authenticationService.hentAppServerUrl(forsyningId).pipe(
                switchMap((url) => this.getFeatures(convertAppserverUrlToLocalProxy((url || "").trim()))),
                catchError(() => of(false))
            );
        }
        return this.getFeatures(baseUrl);
    }

    private getFeatures(baseUrl: string): Observable<boolean> {
        if (!baseUrl) {
            return of(false);
        }
        const endpoint = `${baseUrl.replace(/\/?$/, "/")}api/teknikerpwdfeatures`;
        return this.http.get<{ supported?: boolean; Supported?: boolean }>(endpoint).pipe(
            map((body) => body?.supported === true || body?.Supported === true),
            catchError(() => of(false))
        );
    }

    private cacheKey(forsyningId: string, appServerUrl: string): string {
        if (forsyningId) {
            return `${CACHE_PREFIX}${forsyningId.toLowerCase()}`;
        }
        if (appServerUrl) {
            return `${CACHE_PREFIX}url:${appServerUrl.toLowerCase()}`;
        }
        return "";
    }

    private readCache(key: string): boolean | null {
        const value = this.sessionStorage.getItem(key);
        if (value === "1") {
            return true;
        }
        // Legacy: tidligere skrev vi "0" — ignorer så værkvalg kan prøve igen.
        if (value === "0") {
            this.clearCache(key);
            return null;
        }
        return null;
    }

    private writeCache(key: string, supported: boolean): void {
        if (supported) {
            this.sessionStorage.setItem(key, "1");
        }
    }

    private clearCache(key: string): void {
        this.sessionStorage.removeItem(key);
    }
}
